// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_muster/recap
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Collection data stays local; only a
// DERIVED-SIGNALS object (points painted/unpainted, recipe frequency, WIP ages, paint
// distributions — see signals.rs) is sent for THIS recap call. Raw notes / photo refs
// never leave the device.
//
// HARDEST guardrail (the FENCE — enforced HERE pre-LLM, mirrored server-side + in UI):
// Muster is CAN-safe, fully observational. The ONE billed call narrates the user's own
// logged figures. There is NO free-text chat surface — the only thing that ever reaches
// the model is the computed signals object. `assertObservational` is the pre-LLM gate:
// it refuses, with NO billed call, any attempt to send free-text / advice-shaped input
// down the AI path. We do not rebuild the brain; we compute signals, gate, call, render.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { CollectionSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_muster";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "refused"
  | "no_store"
  | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

// ── The FENCE (pre-LLM, deterministic) ────────────────────────────────────────────
// Muster describes the user's own logged collection — models, paints, progress. It must
// NEVER give advice, recommend a purchase, or judge. The architecture already removes
// the risk (no chat box; the only payload is the computed signals object). This guard is
// the belt-and-braces: BEFORE any network/billed call, it (1) confirms the payload is a
// well-formed signals object and nothing else, and (2) rejects any advice/recommendation/
// purchase request that somehow reached this layer. A refusal here costs ZERO — no call
// is made. This is the "pre-LLM where it says refuse" requirement, made real.
// 🔴 REWRITTEN 2026-08-28 (deep pass). The original set was anchored on a handful of CONTIGUOUS
// openers ("should i", "is this/that/it/my/the …"), so it only fired when the question happened to
// start the way the author imagined. Measured against 27 natural phrasings of exactly the intents
// this app says it refuses, **20 walked straight through** — "Are Terminators good in the current
// meta?", "Rate my army", "Best primer for plastic?", "Help me choose a colour scheme", "Would
// Terminators be better than Aggressors?", "Pick a paint for my Terminators". Since chat's
// graduation every message goes to the model, so a leak here is a BILLED call on a question the
// product has publicly said it will not answer.
//
// The rewrite is INTENT-shaped, not phrase-shaped: it tolerates an intervening object between the
// verb and its complement, covers the plural/past/conditional copulas, and adds the imperative
// advice verbs that were missing entirely. Per this file's own stated philosophy, a false POSITIVE
// is the safe failure mode — Muster has no advisory surface at all, so any advice intent is out of
// scope outright. The deliberate asymmetry: the user's OWN logged facts stay observational, which
// is why superlatives of MEASURE ("most", "longest", "oldest", "largest") are NOT fenced while
// superlatives of QUALITY ("best", "worst", "better") are. fence-battery.mjs locks both halves.
const ADVICE_PATTERNS = [
  /\bshould i\b/i,
  /\bwhat (should|paint|colou?r|scheme|army|miniature|model)\b/i,
  /\brecommend|suggest|advice|advise\b/i,
  /\bworth (buying|getting|painting|it)\b|\bwhich .*\b(better|best|strong(?:er|est)?|weak(?:er|est)?|good|worst|competitive|viable|meta)\b/i,
  /\bhow (do|to|should) i\b/i,
  /\bwhat'?s? (the )?(best|next)\b/i,
  // QUALITY judgement of an army / unit / paint / scheme, with the object allowed to sit between
  // the copula and its complement: "is this a good army?", "ARE Terminators good?", "would X be
  // better than Y?", "was that a good buy?", "tell me if my list is competitive".
  /\b(is|are|was|were|would|could|do|does)\b[^?.!]{0,40}\b(any good|good|bad|better|best|worst|strong(?:er|est)?|weak(?:er|est)?|worth|competitive|viable|meta|op|overpowered|underpowered)\b/i,
  // Bare superlatives/comparatives of QUALITY — a fragment is still an ask ("Best primer for
  // plastic?", "Better to prime black or white?"). Measure words (most/longest/oldest) are
  // deliberately absent: those describe the user's own logged pile and ARE the product.
  /\b(best|better|worst)\b/i,
  // Imperative advice verbs — judgement asked for without a question word at all.
  /\b(rate|rank|judge|critique|grade)\b\s+(my|this|these|the|your)\b/i,
  // Choose-for-me: the app never picks a paint, scheme, unit or purchase on the user's behalf.
  /\bhelp me (choose|pick|decide)\b/i,
  /\b(pick|choose|select)\b\s+(a|an|the|my|me)\b/i,
  /\bteach me\b|\bany tips?\b|\btips? (for|on)\b|\bguide me\b/i,
  // Head-to-head matchups.
  /\b(vs\.?|versus)\b|\bwhich (one )?(wins|is stronger|would win)\b/i,
  // Purchase intent, including the bare imperative ("Buy the new codex?") and the where-to-buy ask.
  /^\s*(buy|purchase|order|preorder|pre-order)\b/i,
  /\bwhere (can|do|should) i\b.{0,20}\b(buy|get|order|find)\b/i,
  /\b(perform|performs|plays?)\s+(well|badly|better)\b/i,
];

/** True when `s` reads as an advice/recommendation/purchase request (→ must be refused, no call). */
export function isAdviceRequest(s: string): boolean {
  const t = (s || "").trim();
  if (!t) return false;
  return ADVICE_PATTERNS.some((re) => re.test(t));
}

/**
 * Pre-LLM observational gate. Throws BrainError("refused") WITHOUT making any call when:
 *  - the payload is not a recognizable signals object, or
 *  - any string carried inside it reads as an advice/recommendation request.
 * On success it returns nothing (caller proceeds to the single billed call).
 */
export function assertObservational(signals: CollectionSignals): void {
  if (!signals || typeof signals !== "object" || !signals.schema || !signals.totals) {
    throw new BrainError("refused", "Muster only describes your logged collection.");
  }
  // Defensive scan: the signals object is all numbers + a few names we computed, but if any
  // user-typed string ever flowed in (faction/recipe/paint names), make sure none is an
  // advice prompt smuggled into a name field. Names are descriptive labels, not questions.
  const strings: string[] = [];
  for (const f of signals.factions ?? []) { strings.push(f.name, f.game_system); }
  for (const r of signals.recipes ?? []) { strings.push(r.name); }
  for (const p of signals.paints?.by_color_family ?? []) { strings.push(p.name); }
  for (const p of signals.paints?.by_brand ?? []) { strings.push(p.name); }
  for (const w of signals.wip?.items ?? []) { strings.push(w.name); }
  if (strings.some(isAdviceRequest)) {
    throw new BrainError("refused", "Muster describes your collection — it can't give advice or recommendations.");
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface CollectionRecap {
  recap: string;
}

/**
 * Collection recap — the ONE AI feature, ONE billed call per action.
 *
 * Sends ONLY the on-device DERIVED-SIGNALS object (points painted/unpainted, per-status
 * counts, recipe-use frequency, WIP ages, paint distributions — see store.ts / signals.rs),
 * never the raw catalog. The brain narrates the figures the user couldn't tally at a glance
 * (e.g. "1,840 points painted vs 920 unpainted; your most-used recipe is on 23 models;
 * two units have been WIP over a year").
 *
 * The server prompt is OBSERVATIONAL ONLY — it names the figures, never advises, never
 * recommends a purchase, never judges. The pre-LLM `assertObservational` gate refuses any
 * non-signals / advice input here, before the billed call is made.
 *
 * Endpoint: POST /api/apps/oriora_muster/recap (Bearer JWT).
 */
export async function recapCollection(signals: CollectionSignals): Promise<CollectionRecap> {
  // FENCE first — refuse (no billed call) before anything reaches the model.
  assertObservational(signals);
  if (signals.totals.units === 0) {
    throw new BrainError("unavailable", "Add a few units first — there's nothing to recap yet.");
  }
  const r = await brainCall<Partial<CollectionRecap>>("recap", { signals });
  return { recap: String(r.recap ?? "").trim() };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_muster/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile. Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must
// allowlist `oriora-muster` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
